import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Cell,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import { useOrderHistory } from '../../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../../stores/appCostPriceCategoriesStore';
import { useSubrecipesStore } from '../../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../../stores/appRestaurantStore';
import { useAppUserDefaultStore } from '../../../../stores/appUserDefaultStore';
import { useTimeIntervalStore } from '../../../../stores/appTimeIntervalStore';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadCostPrice,
  useLoadSubrecipes,
} from '../../../../utils/dataLoadingUtils';
import { enrichCostPriceListWithCurrentPrices } from '../../../../utils/costPriceUtils';
import { resolveWastePercentage } from '../../../../utils/wasteUtils';
import { useByRestaurantCostData } from '../../../../hooks/useByRestaurantCostData';
import { eur } from '../../../../components/orderhistory/utils';
import { rangeFromTimeControls } from '../../../../utils/reports/shared';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import { SectionCard, EmptyState, EmptyCard, ReportLoading, ReportTable, ReportColumn, pctAbs, CHART_GREEN, CHART_RED, CHART_BLUE, CHART_AMBER, useChartTheme } from './common';
import {
  computeCostPriceReport,
  CostPriceCategoryGroup,
  GapBreakdown,
  CoverageRow,
  QuadrantSection,
  Quadrant,
} from '../../../../utils/reports/costPriceReport';

/** Format a signed percentage-point delta like "+3.2pp". */
const ppSigned = (value: number): string => `${value >= 0 ? '+' : ''}${value.toFixed(1)}pp`;

const CostPriceReport: React.FC = () => {
  const { t } = useTranslation();

  // Data is scoped to the selected restaurant by these hooks.
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();

  const orders = useOrderHistory((s) => s.orderHistoryList);
  const isLoadingOrders = useOrderHistory((s) => s.isLoading);
  const revenue = useRevenueHistory((s) => s.revenueHistoryList);
  const isLoadingRevenue = useRevenueHistory((s) => s.isLoading);
  const costPriceList = useCostPriceStore((s) => s.costPriceList);
  const isLoadingCostPrice = useCostPriceStore((s) => s.isLoading);
  const customIngredients = useCostPriceStore((s) => s.customIngredients);
  const categories = useCostPriceCategoriesStore((s) => s.categories);
  const subrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const restaurantId = useAppUserDefaultStore((s) => s.restaurant?.id) ?? '';
  const showExVat = useAppUserDefaultStore((s) => s.showExVatOrders);
  const reportSettings = useRestaurantStore((s) => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const defaultWastePercentage = useMemo(
    () => resolveWastePercentage(reportSettings?.wastePercentage),
    [reportSettings]
  );

  // Cross-restaurant sourcing maps, exactly as the cost-price page wires them.
  const byRestaurant = useByRestaurantCostData(costPriceList, restaurantId);

  // CURRENT enrichment over the full lists — same call chain as CostPricePageNew.
  // Cached across mounts so navigating away and back doesn't re-run the enrichment.
  const enriched = cachedReportValue(
    'costprice.enriched',
    [costPriceList, orders, customIngredients, showExVat, subrecipesById, revenue, byRestaurant, restaurantId, defaultWastePercentage],
    () =>
      enrichCostPriceListWithCurrentPrices(costPriceList, orders, {
        customIngredients,
        showExVat,
        allSubrecipes: subrecipesById,
        revenueHistory: revenue,
        byRestaurant,
        currentRestaurantId: restaurantId,
        defaultWastePercentage,
      })
  );

  const view = useTimeIntervalStore((s) => s.view);
  const startDate = useTimeIntervalStore((s) => s.startDate);
  const endDate = useTimeIntervalStore((s) => s.endDate);

  const data = cachedReportValue(
    'costprice.data',
    [enriched, revenue, orders, categories, showExVat, view, startDate, endDate],
    () => {
      const range = rangeFromTimeControls(view, startDate, endDate);
      return computeCostPriceReport({ enriched, revenue, orders, categories, showExVat, range });
    }
  );

  // While the report's stores are still loading and empty, show a spinner instead
  // of flashing the "no cost prices" explainer.
  if (
    (isLoadingOrders && orders.length === 0) ||
    (isLoadingRevenue && revenue.length === 0) ||
    (isLoadingCostPrice && costPriceList.length === 0)
  ) {
    return <ReportLoading />;
  }

  if (!data.hasCostPrices) {
    return <EmptyCard message={t('reports.costprice.empty')} />;
  }

  return (
    <div className="mt-4">
      {/* 1. Theoretical vs actual gap */}
      <GapSection gap={data.theoreticalVsActual} />

      {/* 2. Dishes per category (sales-weighted) */}
      <SectionCard title={t('reports.costprice.categories.title')} subtitle={t('reports.costprice.categories.subtitle')}>
        {data.categories.length === 0 ? (
          <EmptyState message={t('reports.costprice.categories.empty')} />
        ) : (
          <div className="space-y-6">
            {data.categories.map((category) => (
              <CategoryTable key={category.id} category={category} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* 3. Quadrant section (menu engineering) */}
      <QuadrantsSection quadrants={data.quadrants} />

      {/* 4. Coverage — sold items with no cost price */}
      <SectionCard title={t('reports.costprice.coverage.title')} subtitle={t('reports.costprice.coverage.subtitle')}>
        {data.coverage.length === 0 ? (
          <EmptyState message={t('reports.costprice.coverage.empty')} />
        ) : (
          <CoverageTable rows={data.coverage} />
        )}
      </SectionCard>
    </div>
  );
};

// --- theoretical vs actual ------------------------------------------------

const GapSection: React.FC<{ gap: { total: GapBreakdown; kitchen: GapBreakdown; bar: GapBreakdown; dishesWithRecipeSold: number; dishesWithoutRecipeSold: number } }> = ({
  gap,
}) => {
  const { t } = useTranslation();
  const sides: Array<{ key: 'kitchen' | 'bar' | 'total'; value: GapBreakdown }> = [
    { key: 'kitchen', value: gap.kitchen },
    { key: 'bar', value: gap.bar },
    { key: 'total', value: gap.total },
  ];

  return (
    <SectionCard
      title={t('reports.costprice.theoretical.title')}
      subtitle={t('reports.costprice.theoretical.subtitle')}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sides.map(({ key, value }) => {
          // A positive gap (actual above theoretical) is bad → red.
          const gapColor =
            value.gapPp > 0.05
              ? 'text-red-600 dark:text-red-400'
              : value.gapPp < -0.05
              ? 'text-green-600 dark:text-green-400'
              : 'text-muted dark:text-muted-dark';
          return (
            <div key={key} className="border border-border dark:border-border-dark rounded-lg p-4">
              <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-3">
                {t(`reports.costprice.theoretical.${key}`)}
              </h3>
              <GapLine label={t('reports.costprice.theoretical.theoretical')} value={pctAbs(value.theoreticalPct)} />
              <GapLine label={t('reports.costprice.theoretical.actual')} value={pctAbs(value.actualPct)} />
              <div className="flex items-center justify-between py-1">
                <span className="text-xs text-muted dark:text-muted-dark">
                  {t('reports.costprice.theoretical.gap')}
                </span>
                <span className={`text-sm font-semibold ${gapColor}`}>{ppSigned(value.gapPp)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted dark:text-muted-dark mt-3">
        {t('reports.costprice.theoretical.dishesWithRecipe', { count: gap.dishesWithRecipeSold })}
        {' · '}
        {t('reports.costprice.theoretical.dishesWithoutRecipe', { count: gap.dishesWithoutRecipeSold })}
      </p>
    </SectionCard>
  );
};

const GapLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs text-muted dark:text-muted-dark">{label}</span>
    <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{value}</span>
  </div>
);

// --- per category ---------------------------------------------------------

const CategoryTable: React.FC<{ category: CostPriceCategoryGroup }> = ({ category }) => {
  const { t } = useTranslation();
  type Dish = CostPriceCategoryGroup['dishes'][number];
  const columns: ReportColumn<Dish>[] = [
    { key: 'dish', header: t('reports.costprice.categories.dish'), render: (d) => d.name },
    { key: 'cost', header: t('reports.costprice.categories.cost'), align: 'right', render: (d) => eur(d.cost) },
    { key: 'menuPrice', header: t('reports.costprice.categories.menuPrice'), align: 'right', render: (d) => eur(d.menuPrice) },
    { key: 'costPct', header: t('reports.costprice.categories.costPct'), align: 'right', render: (d) => pctAbs(d.costPercentage) },
    { key: 'marginPct', header: t('reports.costprice.categories.marginPct'), align: 'right', render: (d) => pctAbs(d.marginPercentage) },
    { key: 'qtySold', header: t('reports.costprice.categories.qtySold'), align: 'right', render: (d) => d.quantitySold },
    { key: 'revenue', header: t('reports.costprice.categories.revenue'), align: 'right', render: (d) => eur(d.revenue) },
    {
      key: 'marginContribution',
      header: t('reports.costprice.categories.marginContribution'),
      align: 'right',
      render: (d) => <span className="font-medium">{eur(d.marginContribution)}</span>,
    },
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-2">
        {category.isDefault ? t('reports.costprice.categories.uncategorised') : category.name}
      </h3>
      <ReportTable
        columns={columns}
        rows={category.dishes}
        rowKey={(d) => d.id}
        footer={
          <tr className="text-xs font-semibold text-textPrimary dark:text-textPrimary-dark">
            <td className="px-3 py-2">{t('reports.costprice.categories.subtotal')}</td>
            <td /><td /><td /><td />
            <td className="px-3 py-2 text-right">{category.totalQuantitySold}</td>
            <td className="px-3 py-2 text-right">{eur(category.totalRevenue)}</td>
            <td className="px-3 py-2 text-right">{eur(category.totalMarginContribution)}</td>
          </tr>
        }
      />
    </div>
  );
};

// --- quadrants ------------------------------------------------------------

const QUADRANT_KEYS: Quadrant[] = ['Star', 'Plowhorse', 'Puzzle', 'Dog'];

const quadrantColor = (q: Quadrant): string => {
  switch (q) {
    case 'Star':
      return CHART_GREEN;
    case 'Puzzle':
      return CHART_BLUE;
    case 'Plowhorse':
      return CHART_AMBER;
    case 'Dog':
      return CHART_RED;
  }
};

/** Quadrant background tints — nearest-token light value, mapped to that token's
 * dark counterpart (green→cardLightGreen-dark, blue→lightBlue-dark; amber/red have
 * no dedicated token so the standard tailwind 900-shade is used). Rendered at a
 * lower opacity in dark mode since the base colours are much more saturated there. */
const QUADRANT_BG: Record<Quadrant, { light: string; dark: string }> = {
  Star: { light: '#EDFCF5', dark: '#064E3B' },
  Puzzle: { light: '#EFF8FF', dark: '#1D3A8A' },
  Plowhorse: { light: '#FFF4E2', dark: '#78350F' },
  Dog: { light: '#FEF3F2', dark: '#7F1D1D' },
};

/** Scatter visualisation of the menu-engineering matrix: X = qty sold (period),
 * Y = margin %, split into the four quadrants by the report's own thresholds. */
const QuadrantScatter: React.FC<{ quadrants: QuadrantSection }> = ({ quadrants }) => {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const points = quadrants.rows.map((r) => ({ x: r.quantitySold, y: r.marginPercentage, name: r.name, quadrant: r.quadrant }));
  if (points.length === 0) return null;

  const maxX = Math.max(...points.map((p) => p.x), quadrants.avgQuantity) * 1.1 || 1;
  const maxY = Math.min(100, Math.max(...points.map((p) => p.y), quadrants.marginThreshold) * 1.1 || 100);
  const minY = Math.max(0, Math.min(...points.map((p) => p.y)) - 5);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-lg shadow-md px-3 py-2 text-xs">
        <p className="font-semibold text-textPrimary dark:text-textPrimary-dark mb-1">{p.name}</p>
        <p className="text-muted dark:text-muted-dark">{t('reports.costprice.quadrants.qtySold')}: {p.x}</p>
        <p className="text-muted dark:text-muted-dark">{t('reports.costprice.quadrants.margin')}: {pctAbs(p.y)}</p>
        <p style={{ color: quadrantColor(p.quadrant) }}>{t(`reports.costprice.quadrants.${p.quadrant.toLowerCase()}`)}</p>
      </div>
    );
  };

  const bgOpacity = theme.darkMode ? 0.25 : 0.5;

  return (
    <div className="mb-5" style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
          {/* Quadrant backgrounds */}
          <ReferenceArea x1={quadrants.avgQuantity} x2={maxX} y1={quadrants.marginThreshold} y2={maxY} fill={theme.darkMode ? QUADRANT_BG.Star.dark : QUADRANT_BG.Star.light} fillOpacity={bgOpacity} />
          <ReferenceArea x1={0} x2={quadrants.avgQuantity} y1={quadrants.marginThreshold} y2={maxY} fill={theme.darkMode ? QUADRANT_BG.Puzzle.dark : QUADRANT_BG.Puzzle.light} fillOpacity={bgOpacity} />
          <ReferenceArea x1={quadrants.avgQuantity} x2={maxX} y1={minY} y2={quadrants.marginThreshold} fill={theme.darkMode ? QUADRANT_BG.Plowhorse.dark : QUADRANT_BG.Plowhorse.light} fillOpacity={bgOpacity} />
          <ReferenceArea x1={0} x2={quadrants.avgQuantity} y1={minY} y2={quadrants.marginThreshold} fill={theme.darkMode ? QUADRANT_BG.Dog.dark : QUADRANT_BG.Dog.light} fillOpacity={bgOpacity} />
          <ReferenceLine x={quadrants.avgQuantity} stroke={theme.referenceLine} strokeDasharray="5 5" />
          <ReferenceLine y={quadrants.marginThreshold} stroke={theme.referenceLine} strokeDasharray="5 5" />
          <XAxis
            type="number"
            dataKey="x"
            name={t('reports.costprice.quadrants.qtySold')}
            domain={[0, maxX]}
            tick={{ fill: theme.axisTick, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="number"
            dataKey="y"
            name={t('reports.costprice.quadrants.margin')}
            domain={[minY, maxY]}
            tickFormatter={(v: number) => `${Math.round(v)}%`}
            tick={{ fill: theme.axisTick, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={44}
          />
          <ZAxis range={[80, 80]} />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3', stroke: theme.gridLine }} />
          <Scatter data={points}>
            {points.map((p, i) => (
              <Cell key={i} fill={quadrantColor(p.quadrant)} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

const QuadrantsSection: React.FC<{ quadrants: QuadrantSection }> = ({ quadrants }) => {
  const { t } = useTranslation();
  if (!quadrants.available) {
    return (
      <SectionCard title={t('reports.costprice.quadrants.title')} subtitle={t('reports.costprice.quadrants.subtitle')}>
        <EmptyState message={t('reports.costprice.quadrants.unavailable')} />
      </SectionCard>
    );
  }
  return (
    <SectionCard title={t('reports.costprice.quadrants.title')} subtitle={t('reports.costprice.quadrants.subtitle')}>
      <QuadrantScatter quadrants={quadrants} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {QUADRANT_KEYS.map((quadrant) => {
          const rows = quadrants.rows.filter((r) => r.quadrant === quadrant);
          const quadrantColumns: ReportColumn<typeof rows[number]>[] = [
            { key: 'dish', header: t('reports.costprice.quadrants.dish'), render: (r) => r.name },
            { key: 'qtySold', header: t('reports.costprice.quadrants.qtySold'), align: 'right', render: (r) => r.quantitySold },
            { key: 'margin', header: t('reports.costprice.quadrants.margin'), align: 'right', render: (r) => pctAbs(r.marginPercentage) },
          ];
          return (
            <div key={quadrant} className="border border-border dark:border-border-dark rounded-lg p-4">
              <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-1">
                {t(`reports.costprice.quadrants.${quadrant.toLowerCase()}`)}
              </h3>
              <p className="text-xs text-muted dark:text-muted-dark mb-2">
                {t(`reports.costprice.quadrants.${quadrant.toLowerCase()}Hint`)}
              </p>
              {rows.length === 0 ? (
                <EmptyState message={t('reports.costprice.quadrants.empty')} />
              ) : (
                <ReportTable columns={quadrantColumns} rows={rows} rowKey={(r) => r.id} />
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted dark:text-muted-dark mt-3">
        {t('reports.costprice.quadrants.footnote', {
          qty: quadrants.avgQuantity.toFixed(1),
          margin: quadrants.marginThreshold,
        })}
      </p>
    </SectionCard>
  );
};

// --- coverage -------------------------------------------------------------

const CoverageTable: React.FC<{ rows: CoverageRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  const columns: ReportColumn<CoverageRow>[] = [
    { key: 'item', header: t('reports.costprice.coverage.item'), render: (r) => r.name },
    { key: 'quantity', header: t('reports.costprice.coverage.quantity'), align: 'right', render: (r) => r.quantity },
    { key: 'revenue', header: t('reports.costprice.coverage.revenue'), align: 'right', render: (r) => <span className="font-medium">{eur(r.revenue)}</span> },
  ];
  return <ReportTable columns={columns} rows={rows} rowKey={(r) => r.name} />;
};

export default CostPriceReport;
